import {
  DepositRequestStatus,
  DepositStatus,
} from '@principle-theorem/principle-core/interfaces';

export class DepositStateMachine {
  // Cancelled → Released exists because Stripe's payment_intent.canceled can land before our release finalises.
  private static readonly _requestTransitions: Readonly<
    Record<DepositRequestStatus, ReadonlySet<DepositRequestStatus>>
  > = {
    [DepositRequestStatus.Pending]: new Set([
      DepositRequestStatus.Paid,
      DepositRequestStatus.Authorised,
      DepositRequestStatus.Expired,
      DepositRequestStatus.Cancelled,
    ]),
    [DepositRequestStatus.Authorised]: new Set([
      DepositRequestStatus.Paid,
      DepositRequestStatus.Cancelled,
      DepositRequestStatus.Released,
    ]),
    [DepositRequestStatus.Paid]: new Set([
      DepositRequestStatus.Cancelled,
      DepositRequestStatus.Refunded,
    ]),
    [DepositRequestStatus.Expired]: new Set<DepositRequestStatus>(),
    [DepositRequestStatus.Cancelled]: new Set([DepositRequestStatus.Released]),
    [DepositRequestStatus.Released]: new Set<DepositRequestStatus>(),
    [DepositRequestStatus.Refunded]: new Set<DepositRequestStatus>(),
  };

  private static readonly _metadataTransitions: Readonly<
    Record<DepositStatus, ReadonlySet<DepositStatus>>
  > = {
    [DepositStatus.Required]: new Set([
      DepositStatus.Pending,
      DepositStatus.Waived,
      DepositStatus.Authorised,
      DepositStatus.Paid,
    ]),
    [DepositStatus.Pending]: new Set([
      DepositStatus.Paid,
      DepositStatus.Waived,
      DepositStatus.Authorised,
      DepositStatus.Cancelled,
    ]),
    [DepositStatus.Authorised]: new Set([
      DepositStatus.Paid,
      DepositStatus.Cancelled,
      DepositStatus.Released,
    ]),
    [DepositStatus.Paid]: new Set([
      DepositStatus.Refunded,
      DepositStatus.Forfeited,
    ]),
    [DepositStatus.Waived]: new Set([DepositStatus.Required]),
    [DepositStatus.Refunded]: new Set<DepositStatus>(),
    [DepositStatus.Forfeited]: new Set<DepositStatus>(),
    [DepositStatus.Cancelled]: new Set([DepositStatus.Released]),
    [DepositStatus.Released]: new Set<DepositStatus>(),
  };

  // Self-transitions are allowed, so a replayed webhook is a no-op.
  static canRequestTransition(
    from: DepositRequestStatus,
    to: DepositRequestStatus
  ): boolean {
    if (from === to) {
      return true;
    }
    return DepositStateMachine._requestTransitions[from]?.has(to) ?? false;
  }

  static canMetadataTransition(
    from: DepositStatus,
    to: DepositStatus
  ): boolean {
    if (from === to) {
      return true;
    }
    return DepositStateMachine._metadataTransitions[from]?.has(to) ?? false;
  }
}
